import { useEffect, useMemo, useState } from "react";
import { storage } from "../../state/storage.js";
import {
  buildUsageLedger,
  emptyUsageLedger,
  sumUsageSince,
  type UsageBreakdownRow,
  type UsageLedger,
  type UsageRow,
  type UsageTotals,
} from "../../state/usage-ledger.js";
import type { Message } from "@roqeet/core";

const L = {
  title: "AI \uc0ac\uc6a9 \uac00\uacc4\ubd80",
  refresh: "\uc0c8\ub85c\uace0\uce68",
  today: "\uc624\ub298",
  week: "7\uc77c",
  month: "30\uc77c",
  loading: "\ubd88\ub7ec\uc624\ub294 \uc911...",
  empty:
    "\uc544\uc9c1 \ube44\uc6a9 \uae30\ub85d\uc774 \uc5c6\uc2b5\ub2c8\ub2e4. \uc0c8 AI \uc751\ub2f5\ubd80\ud130 \ud1a0\ud070, \ubaa8\ub378, \ud504\ub85c\ubc14\uc774\ub354, \uc608\uc0c1 \ube44\uc6a9\uc774 \uc313\uc785\ub2c8\ub2e4.",
  modelUsage: "\ubaa8\ub378\ubcc4 \uc0ac\uc6a9\ub7c9",
  noModel: "\ubaa8\ub378 \uae30\ub85d \uc5c6\uc74c",
  providerUsage: "\ud504\ub85c\ubc14\uc774\ub354\ubcc4 \uc0ac\uc6a9\ub7c9",
  noProvider: "\ud504\ub85c\ubc14\uc774\ub354 \uae30\ub85d \uc5c6\uc74c",
  recentDays: "\ucd5c\uadfc \uc77c\uc790",
  pricingSources: "\uac00\uaca9 \uadfc\uac70",
  noPricingSources: "\uac00\uaca9 \uadfc\uac70 \uc5c6\uc74c",
  input: "\uc785\ub825",
  output: "\ucd9c\ub825",
  reasoning: "\ucd94\ub860",
  estimated: "\ucd94\uc815",
  turns: "\ud68c",
  footer:
    "provider\uac00 \uc2e4\uc81c usage\ub97c \uc8fc\uba74 \uc2e4\uc81c \ud1a0\ud070\uc744, \uc544\ub2c8\uba74 \uc120\ud0dd\ud55c \ud1a0\ud06c\ub098\uc774\uc800\ub85c \ucd94\uc815\ud55c \uac12\uc744 \uae30\ub85d\ud569\ub2c8\ub2e4. \uac00\uaca9\uc740 \ubaa8\ub378 \uba54\ud0c0\ub370\uc774\ud130 \ub610\ub294 \ud504\ub9ac\uc14b \uac00\uaca9 override \uae30\uc900\uc785\ub2c8\ub2e4.",
};

export function UsageLedgerSection() {
  const [ledger, setLedger] = useState<UsageLedger>(() => emptyUsageLedger());
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const data = (await storage.exportAll()) as { messages?: Message[] };
      setLedger(buildUsageLedger(data.messages ?? []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const summary = useMemo(() => {
    const now = Date.now();
    return {
      today: sumUsageSince(ledger.daily, startOfLocalDay(now)),
      week: sumUsageSince(ledger.daily, now - 7 * 24 * 60 * 60 * 1000),
      month: sumUsageSince(ledger.daily, now - 30 * 24 * 60 * 60 * 1000),
    };
  }, [ledger.daily]);

  const hasRows = ledger.daily.length > 0;

  return (
    <section>
      <h2 className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-ink-faint">
        <span>{L.title}</span>
        <button
          className="rounded border border-line px-2 py-0.5 text-xs font-normal normal-case text-ink-soft hover:bg-bg-raised"
          onClick={() => void reload()}
        >
          {L.refresh}
        </button>
      </h2>
      <div className="rounded-xl border border-line bg-bg-soft">
        <div className="grid grid-cols-3 divide-x divide-line border-b border-line text-center">
          <Metric label={L.today} item={summary.today} />
          <Metric label={L.week} item={summary.week} />
          <Metric label={L.month} item={summary.month} />
        </div>

        {loading ? (
          <p className="px-3 py-3 text-sm text-ink-faint">{L.loading}</p>
        ) : !hasRows ? (
          <p className="px-3 py-3 text-sm text-ink-faint">{L.empty}</p>
        ) : (
          <div className="space-y-4 p-3">
            <BreakdownTable title={L.modelUsage} rows={ledger.models.slice(0, 8)} empty={L.noModel} />
            <BreakdownTable
              title={L.providerUsage}
              rows={ledger.providers.slice(0, 8)}
              empty={L.noProvider}
            />
            <DailyTable rows={ledger.daily.slice(0, 14)} />
            <BreakdownTable
              title={L.pricingSources}
              rows={ledger.pricingSources.slice(0, 6)}
              empty={L.noPricingSources}
              compact
            />
          </div>
        )}

        <p className="border-t border-line px-3 py-2 text-xs text-ink-faint">{L.footer}</p>
      </div>
    </section>
  );
}

function Metric({ label, item }: { label: string; item: UsageTotals }) {
  return (
    <div className="px-2 py-3">
      <div className="text-xs text-ink-faint">{label}</div>
      <div className="mt-1 text-sm font-semibold tabular-nums text-ink">{formatUsd(item.costUsd)}</div>
      <div className="mt-0.5 text-[11px] text-ink-faint">
        {item.turns}
        {L.turns} / {item.total.toLocaleString()} tok
      </div>
      {item.reasoning > 0 && (
        <div className="mt-0.5 text-[10px] text-ink-faint">
          {L.reasoning} {item.reasoning.toLocaleString()} tok
        </div>
      )}
    </div>
  );
}

function BreakdownTable({
  title,
  rows,
  empty,
  compact = false,
}: {
  title: string;
  rows: UsageBreakdownRow[];
  empty: string;
  compact?: boolean;
}) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-ink-soft">{title}</div>
      <div className="overflow-hidden rounded-lg border border-line bg-bg-raised">
        {rows.length === 0 ? (
          <p className="px-3 py-2 text-xs text-ink-faint">{empty}</p>
        ) : (
          rows.map((row) => (
            <div
              key={row.key}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-line px-3 py-2 text-sm last:border-b-0"
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-ink" title={row.label}>
                  {row.label}
                </div>
                {!compact && (
                  <div className="mt-0.5 text-xs text-ink-faint">
                    {L.input} {row.prompt.toLocaleString()} / {L.output} {row.completion.toLocaleString()}
                    {row.reasoning > 0 ? ` / ${L.reasoning} ${row.reasoning.toLocaleString()}` : ""}
                    {row.estimated ? ` / ${L.estimated} ${row.estimated}${L.turns}` : ""}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="font-medium tabular-nums text-ink">{formatUsd(row.costUsd)}</div>
                <div className="text-xs text-ink-faint">
                  {row.turns}
                  {L.turns} / {row.total.toLocaleString()} tok
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function DailyTable({ rows }: { rows: UsageRow[] }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-ink-soft">{L.recentDays}</div>
      <div className="overflow-hidden rounded-lg border border-line bg-bg-raised">
        {rows.map((row) => (
          <div
            key={row.date}
            className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-line px-3 py-2 text-sm last:border-b-0"
          >
            <span className="text-ink">{row.date}</span>
            <span className="text-xs text-ink-faint">
              {row.turns}
              {L.turns} / {row.total.toLocaleString()} tok
              {row.estimated ? ` / ${L.estimated} ${row.estimated}${L.turns}` : ""}
            </span>
            <span className="font-medium tabular-nums text-ink">{formatUsd(row.costUsd)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function startOfLocalDay(time: number): number {
  const d = new Date(time);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function formatUsd(value: number): string {
  if (value === 0) return "$0";
  if (value < 0.0001) return `$${value.toFixed(6)}`;
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}
